import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Input, Select, Table } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import { fieldError, type Problem, toProblem } from '@/lib/problem';
import { ROLE_LABELS, type Role } from '@/lib/auth/types';
import { useAuth } from '@/lib/auth/useAuth';
import { formatDateTime } from '@/lib/format';
import { type AccessEvent, type Account, people } from './api';
import styles from './PeoplePage.module.css';

const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as Role[]).map((role) => ({
  value: role,
  label: ROLE_LABELS[role],
}));

/**
 * Who may sign in, and what they may do. Administrators only; the backend enforces
 * that on every call regardless of whether this page is shown.
 */
export function PeoplePage() {
  const { me } = useAuth();
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [problem, setProblem] = useState<Problem | null>(null);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('VIEWER');
  const [provisionProblem, setProvisionProblem] = useState<Problem | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  const [deactivating, setDeactivating] = useState<Account | null>(null);
  const [reason, setReason] = useState('');

  const show = useCallback(([loadedAccounts, loadedEvents]: [Account[], AccessEvent[]]) => {
    setAccounts(loadedAccounts);
    setEvents(loadedEvents);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([people.all(), people.events()])
      .then((loaded) => {
        if (!cancelled) show(loaded);
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(toProblem(error));
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  /** After a change: the table and the trail both move. */
  async function load() {
    show(await Promise.all([people.all(), people.events()]));
  }

  async function act(action: () => Promise<unknown>) {
    setProblem(null);
    try {
      await action();
      await load();
      return true;
    } catch (error) {
      setProblem(toProblem(error));
      return false;
    }
  }

  async function provision(event: FormEvent) {
    event.preventDefault();
    setProvisionProblem(null);
    setAdded(null);
    try {
      const account = await people.provision(email.trim(), displayName.trim(), role);
      setAdded(account.email);
      setEmail('');
      setDisplayName('');
      setRole('VIEWER');
      await load();
    } catch (error) {
      setProvisionProblem(toProblem(error));
    }
  }

  async function confirmDeactivation(event: FormEvent) {
    event.preventDefault();
    if (!deactivating) return;
    if (await act(() => people.deactivate(deactivating.userId, reason.trim()))) {
      setDeactivating(null);
      setReason('');
    }
  }

  const rows = (accounts ?? []).map((account) => ({
    key: account.userId,
    cells: {
      name: (
        <span className={styles.person}>
          <span className={styles.personName}>{account.displayName}</span>
          <span className={styles.muted}>{account.email}</span>
        </span>
      ),
      role: (
        <div className={styles.roleCell}>
        <Select
          aria-label={`Role for ${account.displayName}`}
          options={ROLE_OPTIONS}
          value={account.role}
          disabled={!account.active}
          onChange={(event) =>
            void act(() => people.changeRole(account.userId, event.target.value as Role))
          }
        />
        </div>
      ),
      status: account.active ? (
        account.linked ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="neutral">Not yet signed in</Badge>
        )
      ) : (
        <Badge tone="danger">Deactivated</Badge>
      ),
      action:
        account.userId === me?.userId ? (
          <span className={styles.muted}>You</span>
        ) : account.active ? (
          <Button size="sm" variant="ghost" onClick={() => setDeactivating(account)}>
            Deactivate
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => void act(() => people.reactivate(account.userId))}>
            Reactivate
          </Button>
        ),
    },
  }));

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h1 className={styles.title}>People</h1>
        <p className={styles.muted}>
          Signing in with Microsoft links an existing account; it never creates one. Add
          somebody here before they sign in, using the address their Microsoft account uses.
        </p>
      </div>

      {problem && <ProblemAlert problem={problem} title="That change was not made" />}

      <section aria-labelledby="accounts">
        <h2 id="accounts" className={styles.sectionTitle}>
          Accounts
        </h2>
        {accounts === null && !problem ? (
          <p className={styles.muted}>Loading accounts.</p>
        ) : (
          <Table
            caption="Everybody with an account"
            columns={[
              { key: 'name', header: 'Person' },
              { key: 'role', header: 'Role' },
              { key: 'status', header: 'Status', nowrap: true },
              { key: 'action', header: '' },
            ]}
            rows={rows}
            emptyMessage="Nobody has an account yet."
          />
        )}

        {deactivating && (
          <form className={styles.panel} onSubmit={confirmDeactivation}>
            <h3 className={styles.panelTitle}>Deactivate {deactivating.displayName}</h3>
            <p className={styles.muted}>
              They will be signed out on their next request. Nothing is deleted, and an
              administrator can reactivate the account.
            </p>
            <Input
              label="Reason"
              helpText="Recorded in the access trail."
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <div className={styles.buttons}>
              <Button type="submit" disabled={reason.trim() === ''}>
                Deactivate
              </Button>
              <Button type="button" variant="ghost" onClick={() => setDeactivating(null)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </section>

      <section aria-labelledby="add">
        <h2 id="add" className={styles.sectionTitle}>
          Give somebody access
        </h2>
        {added && (
          <Alert tone="success" title="Added">
            {added} can now sign in.
          </Alert>
        )}
        {provisionProblem && <ProblemAlert problem={provisionProblem} title="Not added" />}
        <form className={styles.provision} onSubmit={provision}>
          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            {...fieldError(provisionProblem, 'email')}
          />
          <Input
            label="Display name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            {...fieldError(provisionProblem, 'displayName')}
          />
          <Select
            label="Role"
            options={ROLE_OPTIONS}
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          />
          <div className={styles.buttons}>
            <Button type="submit">Add</Button>
          </div>
        </form>
      </section>

      <section aria-labelledby="trail">
        <h2 id="trail" className={styles.sectionTitle}>
          Recent changes
        </h2>
        <Table
          caption="The most recent changes to who may do what"
          columns={[
            { key: 'when', header: 'When', nowrap: true },
            { key: 'who', header: 'By' },
            { key: 'what', header: 'Change' },
          ]}
          rows={events.map((event, index) => ({
            key: `${event.occurredAt}-${index}`,
            cells: {
              when: formatDateTime(event.occurredAt),
              who: event.actor,
              what: `${event.subject}: ${event.detail}`,
            },
          }))}
          emptyMessage="No changes recorded yet."
        />
      </section>
    </div>
  );
}
